import { useCallback, useEffect } from "react";
import { SUPERADMIN_USER_ID } from "constants/constants";
import { Route, Routes, Navigate, useLocation, Outlet } from "react-router-dom";
import { useUser } from "./context/UserContext";
import { useURLContext } from "./context/URLContext";
import UserLogin from "./pages/user_component/UserLogin";
import Configuration from "./pages/configuration/ConfigurationPage";
import Layout from "./components/layout/Layout";
import SwaggerPage from "./pages/SwaggerPage";
import EvaEntityDetailPage from "./pages/eva/EvaEntityDetailPage";
import EvaToolDetailPage from "./pages/eva/EvaToolDetailPage";
import DarwinEvaListPage from "./pages/eva/EvaListPage";
import EvaEntityCreatePage from "./pages/eva/EvaEntityCreatePage";
import ChatPage from "pages/chat/ChatPage";
import AnalystListPage from "pages/analystList/AnalystListPage";
import AnalystPage from "pages/analyst/AnalystPage";
import ChatSession from "pages/chat/ChatSession";
import { AnalystChatContextProvider } from "context/AnalystChatContext";
import { UIContextProvider } from "context/UIContext";
import { ChatContextProvider } from "context/ChatContext";
import DataConfiguratorContainer from "containers/data_query/DataConfiguratorContainer";
import { init, AuthType } from "@thoughtspot/visual-embed-sdk";
import UserSettingPage from "./pages/user_component/UserSettingPage";
import "katex/dist/katex.min.css";
import ProtectedEvaRoute from "pages/eva/ProtectedEvaRoute";

// Route boundary that scopes ChatContext exclusively to chat and EVA routes.
// Mounts once when the user enters this route group; unmounts when they leave it,
// which resets all ChatContext state automatically (currentChatId, EVA session list, etc.).
//
// Outlet is a React Router placeholder — it renders whichever child route is currently
// active (e.g. <ChatPage /> for /chat/:id, <DarwinEvaListPage /> for /eva/playground).
// Without Outlet, the child routes would match the URL but render nothing.
const ChatContextScope = () => (
  <ChatContextProvider>
    <Outlet />
  </ChatContextProvider>
);

function App() {
  // Access isLoggedIn and reload function from the user context
  const {
    userId, isLoggedIn, tsLoginUsername, tsLoginPassword,
    tsLoginUrl, tsBaseUrl, isLoading, setIsTSInitialized,
  } = useUser();
  const { setPreLoginUrl } = useURLContext();
  const location = useLocation();

  // Authenticates with ThoughtSpot and initialises the embed SDK.
  // Extracted from the useEffect below so the logic is named and readable independently.
  // Only runs for SUPERADMIN_USER_ID — regular users don't have ThoughtSpot access.
  const loginToThoughtSpot = useCallback(async () => {
    // Only proceed if user is logged in and we have ThoughtSpot credentials
    if (!isLoggedIn || !tsLoginUsername || !tsLoginPassword || isLoading) {
      return;
    }
    setIsTSInitialized(false);

    try {
      // Step 1: Login to ThoughtSpot
      const response = await fetch(tsLoginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-By": "XMLHttpRequest",
        },
        credentials: "include",
        body: JSON.stringify({
          username: tsLoginUsername,
          password: tsLoginPassword,
          rememberMe: true,
        }),
      });

      if (!response.ok) {
        console.error("ThoughtSpot login failed: " + response.status + " " + response.statusText);
        return;
      }

      // Step 2: Initialize ThoughtSpot SDK
      init({
        thoughtSpotHost: tsBaseUrl,
        authType: AuthType.None,
      });

      setIsTSInitialized(true);
    } catch (err) {
      console.error("ThoughtSpot login error: " + err);
      setIsTSInitialized(false);
    }
  }, [isLoggedIn, tsLoginUsername, tsLoginPassword, isLoading, tsLoginUrl, tsBaseUrl, setIsTSInitialized]);

  // Trigger ThoughtSpot login when auth state or credentials change
  useEffect(() => {
    if (userId === SUPERADMIN_USER_ID) {
      loginToThoughtSpot();
    }
  }, [loginToThoughtSpot, userId]);

  // Store the current URL when user is not logged in (except for root path)
  useEffect(() => {
    if (!isLoggedIn) {
      const fullPath = location.pathname + location.search;
      // Only store if it's not the root path to avoid unnecessary redirects
      if (fullPath !== "/") {
        setPreLoginUrl(fullPath);
      }
    }
  }, [isLoggedIn, location.pathname, location.search, setPreLoginUrl]);

  const authenticatedRoutes = (
    // UIContext and AnalystChatContext are app-wide: every route including Navbar can read them.
    // ChatContext is route-scoped via ChatContextScope — only mounted for chat and EVA routes.
    <UIContextProvider>
      <AnalystChatContextProvider>
        <Layout>
          <Routes>
            {/* Chat and EVA list routes — ChatContext is scoped to this group.
                EvaListContainer (rendered by DarwinEvaListPage) uses EditChatModal which reads
                currentSessionHeader from ChatContext, so these EVA list routes stay inside. */}
            <Route element={<ChatContextScope />}>
              <Route path="/" element={<ChatPage />} />
              <Route path="/chat/new" element={<ChatPage />} />
              <Route path="/chat/:id?" element={<ChatPage />} />
              <Route path="/eva/agents" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
              <Route path="/eva/tools" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
              <Route path="/eva/playground" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
              <Route path="/eva/experiments" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
              <Route path="/eva/chat/:id" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
              <Route path="/eva/skills" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
            </Route>

            <Route path="/eva" element={
              <ProtectedEvaRoute><Navigate to="/eva/agents" replace /></ProtectedEvaRoute>
            } />
            <Route path="/eva/agent/create" element={
              <ProtectedEvaRoute><EvaEntityCreatePage /></ProtectedEvaRoute>
            } />
            <Route path="/eva/skill/create" element={
              <ProtectedEvaRoute><EvaEntityCreatePage isSkillMode={true} /></ProtectedEvaRoute>
            } />
            <Route path="/eva/skill/:skill_id" element={
              <ProtectedEvaRoute><EvaEntityDetailPage isSkillMode={true} /></ProtectedEvaRoute>
            } />
            <Route path="/eva/:agent_id" element={<ProtectedEvaRoute><EvaEntityDetailPage /></ProtectedEvaRoute>} />
            <Route path="/eva/tool/:tool_id" element={<ProtectedEvaRoute><EvaToolDetailPage /></ProtectedEvaRoute>} />
            <Route path="/user" element={<UserSettingPage />} />
            <Route path="/mkg/*" element={<Configuration />} />
            <Route path="/chat-session" element={<ChatSession />} />
            <Route path="/analyst" element={<AnalystListPage />} />
            <Route path="/analyst/new" element={<AnalystPage />} />
            <Route path="/analyst/:id" element={<AnalystPage />} />
            <Route path="/analyst/:id/jobs" element={<AnalystPage />} />
            <Route path="/analyst/:id/jobs/:jobId" element={<AnalystPage />} />
            <Route path="/analyst/:id/memory" element={<AnalystPage />} />
            <Route path="/data-query/:id?" element={<DataConfiguratorContainer />} />
            <Route path="/docs/swagger" element={<SwaggerPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Layout>
      </AnalystChatContextProvider>
    </UIContextProvider>
  );

  const unauthenticatedRoutes = (
    <Routes>
      <Route path="/*" element={<UserLogin />} />{" "}
    </Routes>
  );

  return (
    <div className="App">
      {isLoggedIn ? authenticatedRoutes : unauthenticatedRoutes}
    </div>
  );
}

export default App;
